import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { Check, Mail } from 'lucide-react';
import { app, API_BASE_URL } from '../../firebase/firebaseConfig';
import ErrorModal from '../ui/ErrorModal';
import {
  CenteredWelcomeShell,
  HOtpCell,
  HPrimaryButton,
  HSecondaryButton,
  HGhostButton,
  HButtonPair,
} from '../h';

/* v9 mockup 1.2 — Check your email / passcode entry.

   Behaviour preserved from the previous implementation:
   - 6-digit code split into per-cell inputs
   - Auto-advance to next cell on digit entry, backspace returns to previous
   - Paste a 6-digit string into any cell distributes across all six
   - Hidden one-time-code input + per-cell autoComplete="one-time-code" supports
     iOS SMS autofill (we keep the same trick the old code used)
   - POST /user/verify; on success signInWithCustomToken; error[0] === 4 means
     "incorrect code", error[0] === 5 means "expired"
   New behaviour:
   - "Send code again" (a soft pill under the code boxes) calls /user/start.
     While the code is still valid the server re-sends the same one through a
     different email provider (codeReused), so any copy that arrives works.
     Otherwise it issues a new code. Success notices start with a tick.
   - The secondary button says "Change email" rather than "Back", so that
     someone waiting for a code is not drawn to it. It still calls onGoBack.
   - The resend notice stays while signing in, so the buttons below it don't
     jump. It is cleared only when the code turns out to have expired, behind
     the error modal.
*/
function VerifyCode({ email, onVerificationSuccess, onGoBack }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  // { text, success }: success notices start with a tick.
  const [resendNotice, setResendNotice] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
    window.scrollTo(0, 0);
  }, []);

  const distribute = useCallback((digits) => {
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) next[i] = digits[i] || '';
    setCode(next);
    const firstEmpty = next.findIndex((d) => d === '');
    const focusIdx = firstEmpty === -1 ? 5 : firstEmpty;
    inputRefs.current[focusIdx]?.focus();
  }, []);

  const handleCodeChange = (index, value) => {
    // Pasted (or autofilled) string lands here as a multi-character value.
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      if (digits.length > 1) {
        distribute(digits);
        return;
      }
      return;
    }
    if (value && !/^\d$/.test(value)) return;

    const next = [...code];
    next[index] = value;
    setCode(next);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    distribute(digits);
  };

  /* Hidden iOS autofill input — Safari fills this with the SMS one-time-code
     even when the user can't see it, then we distribute across the visible cells. */
  const handleAutoFill = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    if (value.length > 0) distribute(value);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      setErrorMessage('Please enter all 6 digits.');
      setShowErrorModal(true);
      return;
    }
    setIsVerifying(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const data = await response.json();
      if (data.success) {
        const auth = getAuth(app);
        await signInWithCustomToken(auth, data.token);
        onVerificationSuccess();
        return;
      }
      // Backend uses an error tuple; first element is a numeric kind.
      // eslint-disable-next-line eqeqeq
      if (data.error?.[0] == 4) {
        setErrorMessage("That code didn't match. Double-check the email we sent.");
      // eslint-disable-next-line eqeqeq
      } else if (data.error?.[0] == 5) {
        setErrorMessage('That code has expired. Send a new one and try again.');
        // "It's the same code" is no longer true. The modal covers the screen,
        // so the buttons moving up behind it is not seen.
        setResendNotice(null);
      } else {
        setErrorMessage('Verification failed. Please try again.');
      }
      setShowErrorModal(true);
    } catch (err) {
      console.error('verify error', err);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const resend = async () => {
    setResendNotice(null);
    setIsResending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(`/user/start returned ${response.status}`);
      const data = await response.json();
      if (data.allowed) {
        setResendNotice({
          success: true,
          text: data.codeReused
            ? 'Sent again a different way. It’s the same code, so use whichever email arrives first.'
            : 'A new code is on its way. Use the one in the newest email.',
        });
      } else {
        setResendNotice({ success: false, text: 'Could not resend — please go back and try a different email.' });
      }
    } catch (err) {
      console.error('resend error', err);
      setResendNotice({ success: false, text: 'Could not resend. Please try again.' });
    } finally {
      setIsResending(false);
    }
  };

  const codeJoined = code.join('');
  const firstEmpty = code.findIndex((d) => d === '');

  return (
    <CenteredWelcomeShell
      title="Check your email"
      titleSize="md"
      subtitle={
        <>
          We sent a 6-digit code to{' '}
          <span className="font-semibold text-slate-700">{email}</span>. It expires in 10
          minutes.
        </>
      }
    >
      <form onSubmit={submit}>
        {/* Hidden field captures iOS SMS autofill before user touches the visible cells. */}
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={handleAutoFill}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="flex gap-2 mb-3 justify-center">
          {code.map((digit, index) => (
            <HOtpCell
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={index === 0 ? undefined : 1}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              active={index === firstEmpty || (firstEmpty === -1 && index === 5)}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {/* mb-0: the app's Bootstrap stylesheet gives every paragraph a 16px bottom margin. */}
        <div className="flex flex-col items-center mt-5 mb-1">
          <p className="mb-0 text-[12px] text-center text-slate-500 max-w-[300px] leading-relaxed">
            Not arrived after a minute? Check your spam folder. Still nothing? We&rsquo;ll email it
            a different way.
          </p>
          <HGhostButton
            type="button"
            onClick={resend}
            disabled={isResending}
            className="mt-3 h-9 gap-1.5 px-4 rounded-full text-[13px] font-semibold bg-slate-100 text-slate-900 hover:bg-slate-200 hover:text-slate-900 active:bg-slate-200"
          >
            <Mail className="w-4 h-4" aria-hidden="true" />
            {isResending ? 'Sending…' : 'Send code again'}
          </HGhostButton>
        </div>

        <div role="status">
          {resendNotice && (
            <p className="mt-3 text-[12px] text-center text-slate-700 font-medium max-w-[300px] mx-auto leading-relaxed">
              {resendNotice.success && (
                <Check
                  className="inline-block w-3.5 h-3.5 mr-1 text-green-600 align-[-2px]"
                  aria-hidden="true"
                />
              )}
              {resendNotice.text}
            </p>
          )}
        </div>

        <HButtonPair className="mt-8">
          <HSecondaryButton type="button" onClick={onGoBack}>
            Change email
          </HSecondaryButton>
          <HPrimaryButton
            type="submit"
            disabled={isVerifying || codeJoined.length !== 6}
          >
            {isVerifying ? 'Signing in…' : 'Sign in'}
          </HPrimaryButton>
        </HButtonPair>
      </form>

      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        title="Verification error"
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
      />
    </CenteredWelcomeShell>
  );
}

export default VerifyCode;
